import {
    type ReactNode,
    Fragment,
    useMemo,
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type IStepperStepProps from "./type";
import type {
    StepperStepNativePropsType,
    StepperStepWebPropsType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import type {
    StepperItemPartContentProps
} from "../../type";
import {
    StepperStepStateContext,
    withStepperItemContext
} from "../../context";
import {
    getStepperMeasures
} from "../../stylesheet";
import StateLayer from "../../../stateLayer";
import StepperMarker from "../marker";
import StepperText from "../text";

const StepperStepContent: FC<StepperItemPartContentProps<IStepperStepProps>> = ({
    customTheme: customThemeProp,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    children,
    context,
    onFocus,
    onPress,
    onBlur,
    style,
    item,
    ...props
}) => {
    const customTheme = customThemeProp ?? context.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled: item.isDisabled,
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const isHorizontal = context.orientation === "horizontal";

    const isInteractive = item.isPressable && !item.isDisabled;

    const measures = getStepperMeasures({
        orientation: context.orientation,
        isInSheet: context.isInSheet,
        size: context.size,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        borders,
        spaces
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        isFocusVisible: isInteractive && isFocusVisible,
        isHorizontal,
        shapeConfigs,
        measures,
        radiuses,
        borders,
        colors,
        spaces
    });

    const isHighlighted = isInteractive && (isHovered || isPressed);

    const stateContext = useMemo(() => ({
        isHighlighted
    }), [
        isHighlighted
    ]);

    const handlePress = () => {
        if(onPress) {
            onPress();

            return;
        }

        context.onStepPress?.(item.index, item.step);
    };

    const webProps: StepperStepWebPropsType = Platform.OS === "web" && item.isCurrent ? {
        "aria-current": "step"
    } : {};

    const nativeProps: StepperStepNativePropsType = Platform.OS === "web" ? {} : {
        accessibilityState: {
            selected: item.isCurrent
        }
    };

    const renderSurface = (): ReactNode => {
        return <StepperStepStateContext.Provider
            value={stateContext}
        >
            <View
                style={[
                    stylesheet.surface,
                    surfaceDynamicStyle
                ]}
            >
                {
                    isInteractive ?
                        <StateLayer
                            borderRadius={radiuses.actions}
                            customTheme={customTheme}
                            overlayColor="default"
                            isHovered={isHovered}
                            isPressed={isPressed}
                        />
                    :
                        null
                }
                {
                    children ?? <Fragment>
                        <StepperMarker />
                        <StepperText />
                    </Fragment>
                }
            </View>
        </StepperStepStateContext.Provider>;
    };

    if(!isInteractive && !item.isDisabled) {
        return <View
            {...webProps}
            {...nativeProps}
            style={[
                style,
                stylesheet.container,
                isHorizontal ? stylesheet.horizontal : stylesheet.vertical,
                containerDynamicStyle
            ]}
            aria-label={item.name}
            accessible={true}
        >
            {renderSurface()}
        </View>;
    }

    return <Pressable
        {...props}
        {...webProps}
        {...nativeProps}
        {...interactionHandlers}
        style={[
            style,
            stylesheet.container,
            isHorizontal ? stylesheet.horizontal : stylesheet.vertical,
            containerDynamicStyle
        ]}
        aria-disabled={item.isDisabled}
        disabled={item.isDisabled}
        aria-label={item.name}
        onPress={handlePress}
        role="button"
    >
        {renderSurface()}
    </Pressable>;
};
export default withStepperItemContext("Step", StepperStepContent);
